import { PDFDocument } from 'pdf-lib';

/**
 * Extracts the page count from a PDF file.
 *
 * @param file - The File object to process
 * @returns Promise<number> - The page count for PDFs (1 on error), or 1 for non-PDF files
 *
 * @example
 * const pageCount = await extractPdfPageCount(file);
 * console.log(`Document has ${pageCount} pages`);
 *
 * @remarks
 * - Returns 1 immediately for non-PDF files (detected by MIME type or extension)
 * - Returns 1 if PDF parsing fails or document is corrupted
 * - Uses pdf-lib library for in-memory PDF parsing
 * - Suitable for files up to 20 MB; larger files may impact performance
 */
export async function extractPdfPageCount(file: File): Promise<number> {
  try {
    // Check if file is a PDF by MIME type or extension
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

    // Return 1 for non-PDF files
    if (!isPdf) {
      return 1;
    }

    // Convert File to ArrayBuffer for pdf-lib processing
    const arrayBuffer = await file.arrayBuffer();

    // Load PDF document
    const pdfDoc = await PDFDocument.load(arrayBuffer);

    // Get page count
    const pageCount = pdfDoc.getPageCount();

    return pageCount > 0 ? pageCount : 1;
  } catch (error) {
    // Log error for debugging but don't throw
    console.warn(`Failed to extract page count from PDF: ${error instanceof Error ? error.message : String(error)}`);

    // Default to 1 on any error (corrupted PDF, unsupported format, etc.)
    return 1;
  }
}
